import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Stamp, AlertTriangle, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VISA = {
  applied: { label: 'Applied', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  expired: { label: 'Expired', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
};

const fmtDep = (d) => {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch { return d; }
};

/**
 * Visa-pending widget — travellers with 'applied' / 'expired' visa on UPCOMING
 * international trips. Tap a row → traveller profile (their per-trip visa).
 * Renders nothing when there's nothing pending. Used on both dashboards.
 */
const VisaPendingWidget = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/dashboard/visa-pending`, { withCredentials: true })
      .then((r) => setRows(Array.isArray(r.data?.items) ? r.data.items : []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading || rows.length === 0) return null;

  const go = (row) => {
    if (row.ucc) navigate(`/clients/${row.ucc}`);
    else if (row.tripObjId) navigate(`/quotes/${row.tripObjId}/edit`);
  };

  return (
    <div className="rounded-2xl border border-amber-200 bg-white overflow-hidden" data-testid="visa-pending-widget">
      <div className="flex items-center gap-2 px-4 py-3 bg-amber-50 border-b border-amber-100">
        <Stamp className="h-4 w-4 text-amber-700" />
        <span className="text-sm font-semibold text-amber-900">Visa Pending</span>
        <span className="ml-1 text-[11px] font-bold bg-amber-200 text-amber-900 rounded-full px-2 py-0.5">{rows.length}</span>
        <span className="ml-auto text-[11px] text-amber-700 hidden sm:inline">Upcoming international trips</span>
      </div>
      <div className="divide-y divide-slate-100 max-h-72 overflow-y-auto">
        {rows.map((r, i) => {
          const v = VISA[r.visaStatus] || VISA.applied;
          const days = r.daysUntil;
          return (
            <div key={`${r.tripId}-${r.ucc || r.name}-${i}`} onClick={() => go(r)} data-testid={`visa-pending-row-${i}`}
              className="px-4 py-3 flex items-center gap-3 hover:bg-slate-50 cursor-pointer">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-slate-900 truncate">{r.name}</div>
                <div className="text-[11px] text-slate-500 truncate">
                  {r.destination || '—'} · {fmtDep(r.departureDate)}
                  {typeof days === 'number' ? ` · ${days < 0 ? 'departed' : days === 0 ? 'today' : `in ${days}d`}` : ''}
                </div>
              </div>
              <span className={`shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${v.cls}`} data-testid={`visa-pending-chip-${i}`}>
                {r.visaStatus === 'expired' && <AlertTriangle className="h-3 w-3" />} Visa: {v.label}
              </span>
              <ChevronRight className="h-4 w-4 text-slate-300 shrink-0" />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default VisaPendingWidget;
